import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Wrench, ChevronDown, Globe, ShoppingBag, Sparkles, CreditCard, Server, Headphones, Activity } from "lucide-react";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import { API } from "@/App";

const STATUS_STYLES = {
  operational: { label: "Opérationnel", color: "text-emerald-300", pill: "bg-emerald-500/10 ring-emerald-400/20", dot: "bg-emerald-400", Icon: CheckCircle2 },
  not_configured: { label: "En cours de configuration", color: "text-amber-300", pill: "bg-amber-500/10 ring-amber-400/20", dot: "bg-amber-400", Icon: AlertTriangle },
  maintenance: { label: "En maintenance", color: "text-amber-300", pill: "bg-amber-500/10 ring-amber-400/20", dot: "bg-amber-400", Icon: Wrench },
  degraded: { label: "Perturbé", color: "text-amber-300", pill: "bg-amber-500/10 ring-amber-400/20", dot: "bg-amber-400", Icon: AlertTriangle },
  outage: { label: "Indisponible", color: "text-red-300", pill: "bg-red-500/10 ring-red-400/20", dot: "bg-red-400", Icon: XCircle },
};

// État général affiché en haut : le pire état parmi tous les services de la liste.
const RANK = { operational: 0, not_configured: 1, maintenance: 1, degraded: 1, outage: 2 };
const OVERALL = [
  { title: "Tous les systèmes fonctionnent normalement", text: "Aucun incident en cours.", tone: "emerald", Icon: CheckCircle2 },
  { title: "Certains services sont perturbés", text: "Une maintenance ou une configuration est en cours.", tone: "amber", Icon: AlertTriangle },
  { title: "Certains services sont indisponibles", text: "Nous travaillons à rétablir le service au plus vite.", tone: "red", Icon: XCircle },
];
const TONES = {
  emerald: { ring: "border-emerald-400/20", glow: "from-emerald-500/20", icon: "bg-emerald-500/15 text-emerald-300", title: "text-emerald-200" },
  amber: { ring: "border-amber-400/20", glow: "from-amber-500/20", icon: "bg-amber-500/15 text-amber-300", title: "text-amber-200" },
  red: { ring: "border-red-400/25", glow: "from-red-500/20", icon: "bg-red-500/15 text-red-300", title: "text-red-200" },
};

const serviceIcon = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("site")) return Globe;
  if (n.includes("boutique")) return ShoppingBag;
  if (n.includes("ia")) return Sparkles;
  if (n.includes("paiement")) return CreditCard;
  return Server;
};

function StatusPill({ status, small }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.outage;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full ring-1 ring-inset ${style.pill} ${style.color} ${small ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"} font-medium`}>
      <span className={`h-1.5 w-1.5 rounded-full ${style.dot} ${status === "operational" ? "" : "animate-pulse"}`} />
      {style.label}
    </span>
  );
}

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState("Boutique");
  const [now, setNow] = useState(Date.now());

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/status`);
      setData(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(interval); clearInterval(tick); };
  }, []);

  const services = data?.services || [];
  const worst = services.reduce((acc, s) => Math.max(acc, RANK[s.status] ?? 2), RANK[data?.overall] ?? 0);
  const overall = OVERALL[worst];
  const tone = TONES[overall.tone];
  const okCount = services.filter((s) => s.status === "operational").length;
  const ago = data ? Math.max(0, Math.round((now - new Date(data.timestamp).getTime()) / 1000)) : null;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />
      <main className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[380px] overflow-hidden">
          <div className={`absolute left-1/2 top-[-200px] h-[400px] w-[820px] -translate-x-1/2 rounded-full bg-gradient-to-b ${tone.glow} to-transparent blur-[90px]`} />
        </div>

        <div className="relative mx-auto max-w-4xl px-5 pb-20 pt-28 sm:px-6">
          <div className="page-rise flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/65">
                <Activity className="h-3.5 w-3.5 text-blue-300" />Statut en direct
              </span>
              <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight">Statut des services</h1>
              <p className="mt-2 text-sm text-white/45">Actualisé automatiquement toutes les 30 secondes.</p>
            </div>
            <button
              onClick={load}
              data-testid="status-refresh"
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2 text-sm text-white/75 transition hover:bg-white/[0.08] hover:text-white"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
          </div>

          {error && (
            <div className="page-rise mt-8 flex items-center gap-3 rounded-2xl border border-red-400/20 bg-red-950/20 p-5 text-sm text-red-200">
              <XCircle className="h-5 w-5 shrink-0" />
              Impossible de contacter l'API pour le moment. Réessayez dans quelques instants.
            </div>
          )}

          {!error && !data && (
            <div className="mt-8 space-y-3">
              {[0, 1, 2].map((n) => <div key={n} className="h-20 animate-pulse rounded-2xl bg-white/[0.03]" />)}
            </div>
          )}

          {!error && data && (
            <>
              <div data-testid="status-overall" className={`page-rise mt-8 flex flex-col gap-5 rounded-3xl border ${tone.ring} bg-white/[0.02] p-6 sm:flex-row sm:items-center sm:p-7`} style={{ animationDelay: "60ms" }}>
                <span className={`relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${tone.icon}`}>
                  <span className="absolute inset-0 animate-ping rounded-2xl bg-current opacity-[0.08]" />
                  <overall.Icon className="h-7 w-7" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`font-heading text-xl font-bold ${tone.title}`}>{overall.title}</p>
                  <p className="mt-1 text-sm text-white/45">{overall.text}</p>
                </div>
                <div className="flex gap-6 text-sm sm:text-right">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/35">Services</p>
                    <p className="mt-0.5 font-heading text-lg font-bold">{okCount}<span className="text-white/35">/{services.length}</span></p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/35">Vérifié</p>
                    <p className="mt-0.5 font-heading text-lg font-bold">{ago < 5 ? "à l'instant" : `il y a ${ago} s`}</p>
                  </div>
                </div>
              </div>

              <h2 className="page-rise mt-10 mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/35" style={{ animationDelay: "100ms" }}>Composants</h2>
              <div className="space-y-3">
                {services.map((service, index) => {
                  const Icon = serviceIcon(service.name);
                  const hasItems = Array.isArray(service.items) && service.items.length > 0;
                  const isOpen = expanded === service.name;
                  return (
                    <div key={service.name} className="page-rise overflow-hidden rounded-2xl border border-white/[0.07] bg-[#0f1019]" style={{ animationDelay: `${120 + index * 50}ms` }}>
                      <button
                        type="button"
                        disabled={!hasItems}
                        onClick={() => hasItems && setExpanded(isOpen ? null : service.name)}
                        className={`flex w-full items-center gap-4 px-5 py-4 text-left ${hasItems ? "transition-colors hover:bg-white/[0.02]" : "cursor-default"}`}
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.05] ring-1 ring-inset ring-white/[0.06]">
                          <Icon className="h-[18px] w-[18px] text-white/70" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[15px] font-medium text-white/90">{service.name}</span>
                          {hasItems && <span className="block text-xs text-white/35">{service.items.length} produit{service.items.length > 1 ? "s" : ""}</span>}
                        </span>
                        <StatusPill status={service.status} />
                        {hasItems && <ChevronDown className={`h-4 w-4 shrink-0 text-white/30 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />}
                      </button>
                      {hasItems && (
                        <div className={`sb-collapse ${isOpen ? "open" : ""}`}><div>
                          <div className="grid gap-2 border-t border-white/[0.05] bg-black/20 p-3 sm:grid-cols-2">
                            {service.items.map((item) => (
                              <div key={item.id || item.name} className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.05] bg-white/[0.02] px-3.5 py-2.5">
                                <span className="truncate text-sm text-white/70">{item.name}</span>
                                <StatusPill status={item.status} small />
                              </div>
                            ))}
                          </div>
                        </div></div>
                      )}
                    </div>
                  );
                })}
              </div>

              <p className="mt-6 text-xs text-white/30">
                Dernière vérification : {new Date(data.timestamp).toLocaleString("fr-FR")}
              </p>

              <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-2xl border border-white/[0.07] bg-gradient-to-br from-blue-600/[0.08] to-violet-600/[0.08] p-6 sm:flex-row sm:items-center">
                <div>
                  <p className="font-semibold">Un problème qui n'apparaît pas ici ?</p>
                  <p className="mt-1 text-sm text-white/45">Ouvre un ticket, l'équipe te répond rapidement.</p>
                </div>
                <Link to="/support" className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#0a0a0f] transition hover:bg-white/90">
                  <Headphones className="h-4 w-4" />Contacter le support
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
